import React from 'react';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, MinusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { DEMAND_LABEL, TREND_LABEL, type Demand, type Trend } from '../../lib/analytics';

/** Shared pieces of a ranked model row, used by the merchant and the supplier page. */

export const RangePicker: React.FC<{
  value: number;
  options: number[];
  onChange: (days: any) => void;
}> = ({ value, options, onChange }) => (
  <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" role="group" aria-label="Period">
    {options.map((d) => (
      <button
        key={d}
        onClick={() => onChange(d)}
        aria-pressed={value === d}
        className={`px-3 py-1.5 text-sm transition-colors ${
          value === d ? 'bg-orange-500 text-black font-semibold' : 'text-white/60 hover:bg-white/[0.06]'
        }`}
      >
        {d} days
      </button>
    ))}
  </div>
);

// Demand is a magnitude: one hue, three strengths, and always the words next to it.
const DEMAND_BARS: Record<Demand, number> = { very_high: 3, high: 2, steady: 1 };

export const DemandBadge: React.FC<{ demand: Demand }> = ({ demand }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-white/70 whitespace-nowrap">
    <span className="flex items-end gap-0.5" aria-hidden="true">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-1 rounded-sm ${i <= DEMAND_BARS[demand] ? 'bg-orange-500' : 'bg-white/15'}`}
          style={{ height: 4 + i * 3 }}
        />
      ))}
    </span>
    {DEMAND_LABEL[demand]}
  </span>
);

const TREND_ICON: Record<Trend, React.ReactNode> = {
  new: <SparklesIcon className="w-4 h-4" />,
  up: <ArrowTrendingUpIcon className="w-4 h-4" />,
  steady: <MinusIcon className="w-4 h-4" />,
  down: <ArrowTrendingDownIcon className="w-4 h-4" />,
};

export const TrendBadge: React.FC<{ trend: Trend }> = ({ trend }) => (
  <span className="inline-flex items-center gap-1 text-xs text-white/50 whitespace-nowrap">
    {TREND_ICON[trend]}
    {TREND_LABEL[trend]}
  </span>
);

interface ModelRowProps {
  rank: number;
  sku: string;
  title: string | null;
  brand: string | null;
  photoUrl: string | null;
  demand: Demand;
  trend: Trend;
  /** Line under the title: where the viewer stands on this model. */
  note?: React.ReactNode;
  action?: React.ReactNode;
  /** False on lists that never have an action (the supplier page). */
  hasActions?: boolean;
}

export const ModelRow: React.FC<ModelRowProps> = ({ rank, sku, title, brand, photoUrl, demand, trend, note, action, hasActions = true }) => (
  <li className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 py-3 border-t border-white/[0.06] first:border-t-0">
    <span className="w-7 text-right text-sm tabular-nums text-white/35 flex-shrink-0">{rank}</span>
    <div className="w-12 h-12 rounded-lg bg-white/[0.05] overflow-hidden flex-shrink-0">
      {photoUrl && <img src={photoUrl} alt="" loading="lazy" className="w-full h-full object-cover" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm text-white truncate">{title || sku}</p>
      <p className="text-xs text-white/40 truncate">
        {[brand, sku].filter(Boolean).join(' · ')}
      </p>
      {note && <p className="text-xs text-white/55 mt-0.5">{note}</p>}
    </div>
    <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0 w-32">
      <DemandBadge demand={demand} />
      <TrendBadge trend={trend} />
    </div>
    {/* Width is reserved even without an action so the demand column lines up down the list. */}
    {hasActions && (
      <div className={`flex-shrink-0 justify-end basis-full sm:basis-auto sm:w-36 ${action ? 'flex' : 'hidden sm:flex'}`}>
        {action}
      </div>
    )}
  </li>
);
